import { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import {
  Users as UsersIcon, BarChart3, FileDown, Megaphone, TrendingUp, TrendingDown,
  ChevronRight, ChevronDown, ExternalLink, Calendar as CalIcon, Pencil, Euro,
} from "lucide-react";
import { ClientPickerButton, PeriodPicker, KpiCard, formatNumber, computePeriodDates } from "./common";
import { SocialDataSkeleton } from "./SocialSkeleton";

// Ritorna la data ISO (YYYY-MM-DD) del lunedì della settimana che contiene `d`.
function isoMondayOf(d = new Date()) {
  const day = d.getDay(); // 0=dom, 1=lun...
  const diff = (day === 0 ? -6 : 1 - day);
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}-${String(m.getDate()).padStart(2, "0")}`;
}
// Ritorna ISO date (YYYY-MM-DD) offsettato di `days` da `iso`.
function isoOffset(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Renderizza un delta inline (+N verde / -N rosso). `value` è il numero, `suffix`
// una stringa come "%" o "". Se value è 0 non mostra nulla.
function DeltaChip({ value, suffix = "", decimals = 0, testid }) {
  if (value === null || value === undefined || !isFinite(value) || value === 0) return null;
  const positive = value > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive ? "text-emerald-600" : "text-red-500";
  const sign = positive ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${cls} ml-1`} data-testid={testid} title="vs settimana scorsa">
      <Icon className="w-2.5 h-2.5" />
      {sign}{value.toFixed(decimals)}{suffix}
    </span>
  );
}

const OPTIMIZATION_GOALS = [
  { v: "all", l: "Tutti gli obiettivi" },
  { v: "LEAD_GENERATION", l: "Lead Generation" },
  { v: "QUALITY_LEAD", l: "Quality Lead" },
  { v: "OFFSITE_CONVERSIONS", l: "Conversioni offsite" },
  { v: "LINK_CLICKS", l: "Click sul link" },
  { v: "REACH", l: "Copertura" },
  { v: "POST_ENGAGEMENT", l: "Engagement sul post" },
  { v: "VIDEO_VIEWS", l: "Visualizzazioni video" },
];

const STATUS_OPTIONS = [
  { v: "ACTIVE", l: "Solo attive" },
  { v: "PAUSED", l: "Solo in pausa" },
  { v: "ALL", l: "Tutte (attive + pausa)" },
];

// Link diretto a Meta/TikTok Ads Manager
function adsManagerUrl({ platform, platformCampaignId, platformAdId }) {
  const p = (platform || "").toLowerCase();
  if (p === "facebook" || p === "instagram" || p === "meta" || p === "metaads") {
    if (platformAdId) return `https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=${platformAdId}`;
    if (platformCampaignId) return `https://business.facebook.com/adsmanager/manage/campaigns?selected_campaign_ids=${platformCampaignId}`;
  }
  if (p === "tiktok") return `https://ads.tiktok.com/i18n/perf/campaign?keyword=${encodeURIComponent(platformCampaignId || "")}`;
  return null;
}

// Calcola le metriche standard di una entità (campagna / adset / ad)
function metricsOf(item) {
  const mt = item.metrics || {};
  const actions = mt.actions || {};
  const leads = Number(actions.lead || actions["onsite_conversion.lead_grouped"] || 0);
  const results = leads + Number(mt.conversions || 0);
  return {
    spend: Number(mt.spend || 0),
    results,
    impressions: Number(mt.impressions || 0),
    reach: Number(mt.reach || 0),
    clicks: Number(mt.clicks || 0),
    ctr: Number(mt.ctr || 0),
  };
}

// Aggrega metriche di una lista di ads (per fabbricare un AdSet o Campagna virtuale)
function aggregateMetrics(items) {
  const agg = { spend: 0, results: 0, impressions: 0, reach: 0, clicks: 0 };
  for (const it of items) {
    const m = metricsOf(it);
    agg.spend += m.spend; agg.results += m.results; agg.impressions += m.impressions;
    agg.reach += m.reach; agg.clicks += m.clicks;
  }
  return agg;
}

// ============== Modal Manual Data ==============
function ManualDataDialog({ open, onOpenChange, clientId, campaign, weeks, initialWeeks, onSaved }) {
  const [rows, setRows] = useState(() => weeks.map(w => ({
    week_start: w,
    appointments: initialWeeks?.[w]?.appointments ?? "",
    revenue: initialWeeks?.[w]?.revenue ?? "",
    notes: initialWeeks?.[w]?.notes ?? "",
  })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(weeks.map(w => ({
      week_start: w,
      appointments: initialWeeks?.[w]?.appointments ?? "",
      revenue: initialWeeks?.[w]?.revenue ?? "",
      notes: initialWeeks?.[w]?.notes ?? "",
    })));
  }, [weeks, initialWeeks]);

  const update = (i, field, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const submit = async () => {
    if (!campaign) return;
    setSaving(true);
    try {
      // Batch parallel POST di tutte le settimane modificate/nuove
      const toSave = rows.filter(r => {
        const appt = Number(r.appointments || 0);
        const rev = Number(r.revenue || 0);
        const wasSet = initialWeeks?.[r.week_start];
        // Skip solo se non era settato e nulla è cambiato
        return wasSet || appt !== 0 || rev !== 0 || (r.notes || "").trim();
      });
      await Promise.all(toSave.map(r => apiFetch("/ads/manual-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: clientId,
          platform_campaign_id: campaign.platformCampaignId,
          week_start: r.week_start,
          appointments: Number(r.appointments || 0),
          revenue: Number(r.revenue || 0),
          notes: r.notes || null,
        }),
      })));
      toast.success("Dati manuali salvati");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Errore salvataggio: " + (e.message || ""));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-data-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Euro className="w-4 h-4 text-emerald-500" /> Dati settimanali · Appuntamenti + Fatturato</DialogTitle>
          <DialogDescription className="truncate">{campaign?.campaignName || "Campagna"}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
          <div className="grid grid-cols-[110px_1fr_1fr_1.5fr] gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-1 pb-1 border-b sticky top-0 bg-white">
            <div>Settimana</div>
            <div>Appuntamenti</div>
            <div>Fatturato €</div>
            <div>Note</div>
          </div>
          {rows.map((r, i) => (
            <div key={r.week_start} className="grid grid-cols-[110px_1fr_1fr_1.5fr] gap-2 py-1.5 border-b last:border-none items-center">
              <div className="text-xs font-mono text-slate-600">{r.week_start}</div>
              <Input type="number" min="0" value={r.appointments} onChange={(e) => update(i, "appointments", e.target.value)} className="h-7 text-xs" data-testid={`manual-appt-${i}`} placeholder="0" />
              <Input type="number" min="0" step="0.01" value={r.revenue} onChange={(e) => update(i, "revenue", e.target.value)} className="h-7 text-xs" data-testid={`manual-rev-${i}`} placeholder="0.00" />
              <Input value={r.notes} onChange={(e) => update(i, "notes", e.target.value)} className="h-7 text-xs" data-testid={`manual-notes-${i}`} placeholder="—" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="manual-cancel">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="manual-save">
            {saving ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Main Tab ==============
export function AdsTab({ clients, selectedClientId, onClientChange }) {
  const clientId = selectedClientId;
  const setClientId = onClientChange;
  const [period, setPeriod] = useState("30d");
  const [goal, setGoal] = useState("all");
  const [status, setStatus] = useState("ACTIVE");
  const [expandedCamp, setExpandedCamp] = useState(() => new Set());
  const [expandedSet, setExpandedSet] = useState(() => new Set());
  const [campaigns, setCampaigns] = useState([]);
  const [ads, setAds] = useState([]);
  const [manualData, setManualData] = useState({ weeks: [], by_campaign: {} });
  // Spend Zernio per settimana (current + previous) per calcolare delta ROI/CAC week-over-week.
  // Mappa: { [platformCampaignId]: spend }
  const [spendCurrWeek, setSpendCurrWeek] = useState({});
  const [spendPrevWeek, setSpendPrevWeek] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, campaign: null });

  const { from, to } = useMemo(() => computePeriodDates(period), [period]);

  const fetchAll = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      // Date settimana corrente e precedente (per delta week-over-week)
      const currMon = isoMondayOf(new Date());
      const prevMon = isoOffset(currMon, -7);
      const prevSun = isoOffset(currMon, -1);
      const todayISO = isoOffset(currMon, 6); // domenica corrente (o oggi se non ancora domenica)

      const [cR, aR, mR, wCurR, wPrevR] = await Promise.all([
        apiFetch(`/zernio/campaigns?client_id=${clientId}&from_date=${from}&to_date=${to}`),
        apiFetch(`/zernio/ads?client_id=${clientId}&from_date=${from}&to_date=${to}`),
        apiFetch(`/ads/manual-data?client_id=${clientId}&from_date=${from}&to_date=${to}`),
        apiFetch(`/zernio/campaigns?client_id=${clientId}&from_date=${currMon}&to_date=${todayISO}`),
        apiFetch(`/zernio/campaigns?client_id=${clientId}&from_date=${prevMon}&to_date=${prevSun}`),
      ]);
      setCampaigns((cR.ok ? await cR.json() : {}).campaigns || []);
      setAds((aR.ok ? await aR.json() : {}).ads || []);
      setManualData(mR.ok ? await mR.json() : { weeks: [], by_campaign: {} });
      // Costruisci mappe spend per settimana
      const curMap = {}, prevMap = {};
      const wCurData = wCurR.ok ? await wCurR.json() : { campaigns: [] };
      const wPrevData = wPrevR.ok ? await wPrevR.json() : { campaigns: [] };
      for (const c of (wCurData.campaigns || [])) curMap[c.platformCampaignId] = Number((c.metrics || {}).spend || 0);
      for (const c of (wPrevData.campaigns || [])) prevMap[c.platformCampaignId] = Number((c.metrics || {}).spend || 0);
      setSpendCurrWeek(curMap);
      setSpendPrevWeek(prevMap);
    } finally { setLoading(false); }
  }, [clientId, from, to]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- Costruzione gerarchia Campagna → AdSet → Ad ----
  const hierarchy = useMemo(() => {
    if (!campaigns.length) return [];
    const seen = new Set();
    const camps = [];
    for (const c of campaigns) {
      const key = c._id || c.platformCampaignId;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      if (status !== "ALL" && (c.platformCampaignStatus || "").toUpperCase() !== status) continue;
      if (goal !== "all" && (c.optimizationGoal || "").toUpperCase() !== goal) continue;
      // Filtra gli ads di questa campagna
      const campAds = ads.filter(a => a.platformCampaignId === c.platformCampaignId);
      // Raggruppa per platformAdSetId
      const adsetMap = new Map();
      for (const a of campAds) {
        const setId = a.platformAdSetId || a.adSetName || "no-adset";
        const setName = a.adSetName || "Gruppo inserzione senza nome";
        if (!adsetMap.has(setId)) adsetMap.set(setId, { platformAdSetId: setId, adSetName: setName, ads: [], adSetBudget: a.adSetBudget });
        adsetMap.get(setId).ads.push(a);
      }
      const adsets = Array.from(adsetMap.values()).map(s => ({
        ...s,
        metrics: aggregateMetrics(s.ads),
      })).sort((x, y) => y.metrics.spend - x.metrics.spend);
      camps.push({ ...c, adsets });
    }
    return camps.sort((a, b) => Number((b.metrics || {}).spend || 0) - Number((a.metrics || {}).spend || 0));
  }, [campaigns, ads, status, goal]);

  // ---- KPI totali (aggregate su campagne filtrate) ----
  const totalKpi = useMemo(() => {
    let spend = 0, results = 0, impressions = 0, reach = 0, clicks = 0;
    let manualAppt = 0, manualRevenue = 0;
    let apptCurW = 0, apptPrevW = 0, revCurW = 0, revPrevW = 0, spendCurW = 0, spendPrevW = 0;
    const currMon = isoMondayOf(new Date());
    const prevMon = isoOffset(currMon, -7);
    for (const c of hierarchy) {
      const m = metricsOf(c);
      spend += m.spend; results += m.results; impressions += m.impressions; reach += m.reach; clicks += m.clicks;
      const md = manualData.by_campaign?.[c.platformCampaignId];
      if (md) {
        manualAppt += Number(md.total_appointments || 0);
        manualRevenue += Number(md.total_revenue || 0);
        const wCur = md.weeks?.[currMon] || {};
        const wPrev = md.weeks?.[prevMon] || {};
        apptCurW += Number(wCur.appointments || 0);
        apptPrevW += Number(wPrev.appointments || 0);
        revCurW += Number(wCur.revenue || 0);
        revPrevW += Number(wPrev.revenue || 0);
      }
      spendCurW += Number(spendCurrWeek[c.platformCampaignId] || 0);
      spendPrevW += Number(spendPrevWeek[c.platformCampaignId] || 0);
    }
    const roiCurW = spendCurW > 0 ? ((revCurW - spendCurW) / spendCurW) * 100 : null;
    const roiPrevW = spendPrevW > 0 ? ((revPrevW - spendPrevW) / spendPrevW) * 100 : null;
    return {
      spend, results, impressions, reach, clicks,
      manualAppt, manualRevenue,
      conversionPct: results > 0 ? (manualAppt / results) * 100 : 0,
      cac: manualAppt > 0 ? spend / manualAppt : 0,
      roi: spend > 0 ? ((manualRevenue - spend) / spend) * 100 : 0,
      cpc: clicks > 0 ? spend / clicks : 0,
      cpm: impressions > 0 ? (spend * 1000) / impressions : 0,
      ctrPct: impressions > 0 ? (clicks / impressions) * 100 : 0,
      cpr: results > 0 ? spend / results : 0,
      // Delta week-over-week (globali)
      deltaApptWow: apptCurW - apptPrevW,
      deltaRoiWow: (roiCurW != null && roiPrevW != null) ? (roiCurW - roiPrevW) : null,
    };
  }, [hierarchy, manualData, spendCurrWeek, spendPrevWeek]);

  const toggleCamp = (k) => setExpandedCamp(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleSet  = (k) => setExpandedSet(prev  => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const openManual = (c) => setEditModal({ open: true, campaign: c });

  const downloadLeadsCsv = async () => {
    if (!clientId) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("session_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/zernio/leads-csv?client_id=${clientId}&from_date=${from}&to_date=${to}`;
      const r = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
      if (!r.ok) { toast.error("Errore download CSV"); return; }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `leads_${clientId}_${from}_${to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("CSV scaricato");
    } finally { setDownloading(false); }
  };

  const downloadAdsReport = async (rptPeriod) => {
    if (!clientId) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("session_token");
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/ads-report/pdf?client_id=${clientId}&period=${rptPeriod}`;
      if (rptPeriod === "custom") url += `&from_date=${from}&to_date=${to}`;
      const r = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
      if (!r.ok) { toast.error("Errore download report"); return; }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ads_report_${rptPeriod}_${clientId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report PDF scaricato");
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <ClientPickerButton clients={clients} value={clientId} onChange={setClientId} testid="ads-client-picker" />
        <PeriodPicker value={period} onChange={setPeriod} testid="ads-period-picker" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-9" data-testid="ads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={goal} onValueChange={setGoal}>
          <SelectTrigger className="w-52 h-9" data-testid="ads-goal-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{OPTIMIZATION_GOALS.map(g => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={downloadLeadsCsv} disabled={!clientId || downloading} data-testid="download-leads-csv">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> {downloading ? "..." : "Scarica lead CSV"}
        </Button>
        <Select onValueChange={(v) => v && downloadAdsReport(v)}>
          <SelectTrigger className="w-44 h-9" data-testid="download-ads-report" disabled={!clientId || downloading}>
            <SelectValue placeholder={downloading ? "Scarico..." : "📊 Scarica Report PDF"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
            <SelectItem value="30d">Ultimi 30 giorni</SelectItem>
            <SelectItem value="custom">Periodo personalizzato (usa il picker)</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">Zernio · {hierarchy.length} campagne</div>
      </div>

      {!clientId && <Card className="p-8 text-center text-sm text-muted-foreground">Seleziona un cliente per vedere le sue campagne.</Card>}
      {clientId && loading && <SocialDataSkeleton rows={5} />}

      {clientId && !loading && (
        <>
          {/* Row 1: KPI Ads (dai dati Zernio) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label={`Contatti (${period})`} value={totalKpi.results} icon={<UsersIcon className="w-4 h-4" />} testid="kpi-ads-leads" />
            <KpiCard label="CPR" value={`€${totalKpi.cpr.toFixed(2)}`} icon={<TrendingUp className="w-4 h-4" />} testid="kpi-ads-cpr" />
            <KpiCard label={`Spesa (${period})`} value={`€${totalKpi.spend.toFixed(2)}`} icon={<Megaphone className="w-4 h-4" />} testid="kpi-ads-spend" />
            <KpiCard label="Impressions" value={totalKpi.impressions} icon={<BarChart3 className="w-4 h-4" />} testid="kpi-ads-impressions" />
          </div>

          {/* Row 2: KPI Business (manuali + derivati) */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white" data-testid="kpi-appointments">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-medium flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" /> Appuntamenti</div>
              <div className="text-2xl font-bold mt-1 font-mono flex items-center">{totalKpi.manualAppt}
                {totalKpi.deltaApptWow !== 0 && (
                  <span className={`ml-1.5 text-[10px] font-semibold inline-flex items-center gap-0.5 ${totalKpi.deltaApptWow > 0 ? "text-emerald-100" : "text-red-200"}`} data-testid="kpi-appointments-delta">
                    {totalKpi.deltaApptWow > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {totalKpi.deltaApptWow > 0 ? "+" : ""}{totalKpi.deltaApptWow}
                  </span>
                )}
              </div>
              <div className="text-[9px] text-white/60 mt-0.5">vs settimana scorsa</div>
            </Card>
            <Card className="p-3 bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white" data-testid="kpi-revenue">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-medium flex items-center gap-1"><Euro className="w-3.5 h-3.5" /> Fatturato</div>
              <div className="text-2xl font-bold mt-1 font-mono">€{formatNumber(totalKpi.manualRevenue)}</div>
            </Card>
            <KpiCard label="Conv. → Appuntamento" value={`${totalKpi.conversionPct.toFixed(1)}%`} icon={<TrendingUp className="w-4 h-4" />} testid="kpi-conversion" />
            <KpiCard label="CAC" value={`€${totalKpi.cac.toFixed(2)}`} icon={<Euro className="w-4 h-4" />} testid="kpi-cac" />
            <Card className={`p-3 ${totalKpi.roi >= 0 ? 'bg-gradient-to-br from-slate-900 to-slate-700' : 'bg-gradient-to-br from-red-600 to-red-700'} text-white`} data-testid="kpi-roi">
              <div className="text-[10px] uppercase tracking-wider text-white/80 font-medium flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> ROI</div>
              <div className="text-2xl font-bold mt-1 font-mono flex items-center">{totalKpi.roi >= 0 ? "+" : ""}{totalKpi.roi.toFixed(1)}%
                {totalKpi.deltaRoiWow != null && (
                  <span className={`ml-1.5 text-[10px] font-semibold inline-flex items-center gap-0.5 ${totalKpi.deltaRoiWow > 0 ? "text-emerald-300" : "text-red-300"}`} data-testid="kpi-roi-delta">
                    {totalKpi.deltaRoiWow > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {totalKpi.deltaRoiWow > 0 ? "+" : ""}{totalKpi.deltaRoiWow.toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="text-[9px] text-white/60 mt-0.5">vs settimana scorsa</div>
            </Card>
          </div>

          {/* Row 3: KPI ancillari */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Reach" value={totalKpi.reach} icon={<UsersIcon className="w-4 h-4" />} />
            <KpiCard label="Clicks" value={totalKpi.clicks} icon={<BarChart3 className="w-4 h-4" />} />
            <KpiCard label="CTR" value={`${totalKpi.ctrPct.toFixed(2)}%`} icon={<TrendingUp className="w-4 h-4" />} />
            <KpiCard label="CPC / CPM" value={`€${totalKpi.cpc.toFixed(2)} / €${totalKpi.cpm.toFixed(2)}`} icon={<BarChart3 className="w-4 h-4" />} />
          </div>

          {/* Hierarchy: Campagne → AdSet → Ad */}
          <Card className="overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b text-sm font-semibold flex items-center justify-between">
              <span>Campagne · {hierarchy.length}</span>
              <span className="text-[10px] text-slate-500 font-normal">Clicca campagna → gruppo inserzione → inserzione</span>
            </div>
            {hierarchy.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Nessuna campagna nel periodo (o filtri selezionati)</div>
            ) : (
              <div>
                {/* Header 12-column grid */}
                <div className="grid grid-cols-[24px_1fr_24px_60px_46px_70px_70px_80px_80px_70px_90px_90px_80px] gap-1.5 px-3 py-2 border-b bg-slate-100/50 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  <div></div><div>Nome</div><div></div><div className="text-center">Status</div><div className="text-right">Ads</div><div className="text-right">Contatti</div><div className="text-right">CPR</div>
                  <div className="text-right">Spesa</div><div className="text-right">Appunt.</div><div className="text-right">Conv%</div><div className="text-right">CAC</div><div className="text-right">Fatt.€</div><div className="text-right">ROI</div>
                </div>
                {hierarchy.map((c, ci) => {
                  const cm = metricsOf(c);
                  const st = c.platformCampaignStatus;
                  const stColor = st === "ACTIVE" ? "text-emerald-600 bg-emerald-50" : st === "PAUSED" ? "text-amber-600 bg-amber-50" : "text-slate-500 bg-slate-100";
                  const campKey = c.platformCampaignId || c._id;
                  const isOpen = expandedCamp.has(campKey);
                  const md = manualData.by_campaign?.[c.platformCampaignId] || { total_appointments: 0, total_revenue: 0, weeks: {} };
                  const appt = Number(md.total_appointments || 0);
                  const rev = Number(md.total_revenue || 0);
                  const conv = cm.results > 0 ? (appt / cm.results) * 100 : 0;
                  const cac = appt > 0 ? cm.spend / appt : 0;
                  const roi = cm.spend > 0 ? ((rev - cm.spend) / cm.spend) * 100 : 0;
                  // ---- Delta week-over-week ----
                  const currMon = isoMondayOf(new Date());
                  const prevMon = isoOffset(currMon, -7);
                  const wCur = md.weeks?.[currMon] || {};
                  const wPrev = md.weeks?.[prevMon] || {};
                  const apptCur = Number(wCur.appointments || 0);
                  const apptPrev = Number(wPrev.appointments || 0);
                  const revCur = Number(wCur.revenue || 0);
                  const revPrev = Number(wPrev.revenue || 0);
                  const spCur = Number(spendCurrWeek[c.platformCampaignId] || 0);
                  const spPrev = Number(spendPrevWeek[c.platformCampaignId] || 0);
                  const roiCur = spCur > 0 ? ((revCur - spCur) / spCur) * 100 : null;
                  const roiPrev = spPrev > 0 ? ((revPrev - spPrev) / spPrev) * 100 : null;
                  const deltaAppt = apptCur - apptPrev;
                  const deltaRoi = (roiCur != null && roiPrev != null) ? (roiCur - roiPrev) : null;
                  const href = adsManagerUrl({ platform: c.platform, platformCampaignId: c.platformCampaignId });
                  return (
                    <div key={campKey || ci} data-testid={`campaign-row-${ci}`}>
                      {/* Campagna */}
                      <div className="grid grid-cols-[24px_1fr_24px_60px_46px_70px_70px_80px_80px_70px_90px_90px_80px] gap-1.5 px-3 py-2 border-b text-xs items-center hover:bg-slate-50 cursor-pointer" onClick={() => toggleCamp(campKey)} data-testid={`campaign-toggle-${ci}`}>
                        <div className="flex items-center justify-center text-slate-500">{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</div>
                        <div className="truncate">
                          <div className="truncate font-medium">{c.campaignName}</div>
                          <div className="text-[10px] text-slate-500 capitalize">{c.platformObjective || c.optimizationGoal || "-"} · {c.adsets.length} gruppi</div>
                        </div>
                        <div className="flex items-center justify-center gap-0.5">
                          {href && <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Apri su Meta Ads Manager" className="text-slate-400 hover:text-indigo-600" data-testid={`campaign-external-${ci}`}><ExternalLink className="w-3 h-3" /></a>}
                          <button onClick={(e) => { e.stopPropagation(); openManual(c); }} title="Inserisci appuntamenti / fatturato" className="text-slate-400 hover:text-emerald-600" data-testid={`campaign-edit-manual-${ci}`}><Pencil className="w-3 h-3" /></button>
                        </div>
                        <div className="text-center"><span className={`inline-block px-1 py-0.5 rounded text-[9px] font-semibold ${stColor}`}>{st || "-"}</span></div>
                        <div className="text-right font-mono">{c.adCount || 0}</div>
                        <div className="text-right font-mono font-semibold text-emerald-600">{cm.results}</div>
                        <div className="text-right font-mono">{cm.results ? `€${(cm.spend / cm.results).toFixed(2)}` : "-"}</div>
                        <div className="text-right font-mono font-semibold">€{cm.spend.toFixed(2)}</div>
                        <div className="text-right font-mono font-semibold text-emerald-700 flex items-center justify-end">{appt}<DeltaChip value={deltaAppt} testid={`campaign-appt-delta-${ci}`} /></div>
                        <div className="text-right font-mono">{cm.results ? `${conv.toFixed(1)}%` : "-"}</div>
                        <div className="text-right font-mono">{appt ? `€${cac.toFixed(2)}` : "-"}</div>
                        <div className="text-right font-mono font-semibold">€{rev.toFixed(0)}</div>
                        <div className={`text-right font-mono font-semibold flex items-center justify-end ${cm.spend > 0 ? (roi >= 0 ? "text-emerald-600" : "text-red-500") : "text-slate-400"}`}>{cm.spend ? `${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%` : "-"}<DeltaChip value={deltaRoi} suffix="%" decimals={1} testid={`campaign-roi-delta-${ci}`} /></div>
                      </div>

                      {/* AdSet livello 2 */}
                      {isOpen && (
                        <div className="bg-slate-50/40 border-b">
                          {c.adsets.length === 0 ? (
                            <div className="px-8 py-3 text-[11px] text-slate-500 italic">Nessun gruppo inserzione nel periodo per questa campagna.</div>
                          ) : c.adsets.map((s, si) => {
                            const setKey = `${campKey}::${s.platformAdSetId}`;
                            const setOpen = expandedSet.has(setKey);
                            const sm = s.metrics;
                            return (
                              <div key={setKey} data-testid={`adset-row-${ci}-${si}`}>
                                <div className="grid grid-cols-[24px_1fr_24px_60px_46px_70px_70px_80px_80px_70px_90px_90px_80px] gap-1.5 px-3 py-1.5 border-b border-dashed border-slate-200 text-[11px] items-center hover:bg-white cursor-pointer" onClick={() => toggleSet(setKey)} data-testid={`adset-toggle-${ci}-${si}`}>
                                  <div className="flex items-center justify-center text-slate-400 pl-4">{setOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</div>
                                  <div className="truncate pl-3">
                                    <div className="truncate text-slate-700">{s.adSetName}</div>
                                    <div className="text-[9px] text-slate-500">{s.ads.length} inserzioni {s.adSetBudget ? `· budget €${s.adSetBudget}` : ""}</div>
                                  </div>
                                  <div></div><div></div>
                                  <div className="text-right font-mono">{s.ads.length}</div>
                                  <div className="text-right font-mono text-emerald-600">{sm.results}</div>
                                  <div className="text-right font-mono">{sm.results ? `€${(sm.spend / sm.results).toFixed(2)}` : "-"}</div>
                                  <div className="text-right font-mono">€{sm.spend.toFixed(2)}</div>
                                  <div></div><div></div><div></div><div></div><div></div>
                                </div>

                                {/* Ad livello 3 */}
                                {setOpen && s.ads.map((a, ai) => {
                                  const am = metricsOf(a);
                                  const ast = (a.platformStatus || "").toUpperCase();
                                  const astColor = ast === "ACTIVE" ? "text-emerald-600 bg-emerald-50" : ast === "PAUSED" ? "text-amber-600 bg-amber-50" : "text-slate-500 bg-slate-100";
                                  const adHref = adsManagerUrl({ platform: a.platform, platformCampaignId: a.platformCampaignId, platformAdId: a.platformAdId });
                                  return (
                                    <div key={a._id || ai} className="grid grid-cols-[24px_1fr_24px_60px_46px_70px_70px_80px_80px_70px_90px_90px_80px] gap-1.5 px-3 py-1 border-b border-dotted border-slate-200 text-[10px] items-center hover:bg-white" data-testid={`ad-row-${ci}-${si}-${ai}`}>
                                      <div></div>
                                      <div className="truncate pl-8"><div className="truncate">{a.name || "-"}</div><div className="text-[9px] text-slate-500 capitalize">{a.platform}</div></div>
                                      <div className="flex items-center justify-center">{adHref && <a href={adHref} target="_blank" rel="noreferrer" title="Apri inserzione" className="text-slate-400 hover:text-indigo-600"><ExternalLink className="w-3 h-3" /></a>}</div>
                                      <div className="text-center"><span className={`inline-block px-1 py-0.5 rounded text-[8px] font-semibold ${astColor}`}>{ast || "-"}</span></div>
                                      <div></div>
                                      <div className="text-right font-mono text-emerald-600">{am.results}</div>
                                      <div className="text-right font-mono">{am.results ? `€${(am.spend / am.results).toFixed(2)}` : "-"}</div>
                                      <div className="text-right font-mono">€{am.spend.toFixed(2)}</div>
                                      <div></div><div></div><div></div><div></div><div></div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Modal dati manuali */}
      {editModal.open && (
        <ManualDataDialog
          open={editModal.open}
          onOpenChange={(v) => setEditModal({ open: v, campaign: v ? editModal.campaign : null })}
          clientId={clientId}
          campaign={editModal.campaign}
          weeks={manualData.weeks || []}
          initialWeeks={manualData.by_campaign?.[editModal.campaign?.platformCampaignId]?.weeks || {}}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}
